// What History's table shows, and how it is searched, filtered and sorted.
//
// Pure — no Vue, no DOM, no store and no clock. A ROW is a plain object the
// page derives once per carbon copy or proposal; everything else in here is
// functions over arrays of those. That is what lets the list logic be tested
// without mounting a page, and what keeps HistoryPage.vue about rendering
// rather than about arithmetic.
//
// THE ROW IS FLAT AND ALREADY PRINTED. `sportsText` is a string, not a list of
// legs to walk at render time, and `at` is a number, not a date to parse inside
// a comparator. A filter or a sort that had to reach back through the bet
// record into the frozen multi and its legs would be three lookups per row per
// keystroke, on the one screen that can hold a season of them.

import { TIER_LABEL } from './format.js';
import { canonicalStrategyId } from './engine/strategies/aliases.js';
import { legsOverdue, INTERIM_NOTE } from './results-due.js';

/**
 * The five words a result is ever printed as. One copy, three components.
 *
 * 'unresulted' is the one of them that is NEVER STORED. A bet's `result` stays
 * 'pending' and a proposal's `outcome` stays 'pending' on the record; the row
 * is where "no score came back" is decided, because it is a fact about the
 * clock rather than about the bet, and it becomes true on its own at 07:00 one
 * morning without anybody writing anything.
 */
export const RESULT_LABEL = {
  pending: 'Pending',
  unresulted: 'No result',
  won: 'Won',
  lost: 'Lost',
  void: 'Void',
};

/**
 * WHO DECIDED THIS ROW, in three or four words.
 *
 * A stamp says what happened; this says where that came from, which is the
 * question somebody asks when they disagree with it. "Settled from scores" is
 * the app reading a result feed — it has NOT read your bookmaker's account, and
 * a punter who wants the payout checked has to check it. "Settled by you" is a
 * call somebody made themselves, which is also the one the auto-settle will
 * never overwrite.
 *
 * `cron` is here because the morning job is a third answer a row could one day
 * carry. Nothing stamps it today — results merged in-app read as `auto`, and
 * that is right: they came from scores either way.
 */
export const SETTLED_BY = {
  auto: 'Settled from scores',
  manual: 'Settled by you',
  cron: 'Settled by the morning cron',
};

/**
 * The same sentence for a race, because a race has no score.
 *
 * "Settled from scores" on a ticket whose legs are horses reads as the app not
 * knowing what it settled, and the distinction is real: a racing leg is settled
 * from the official result — placings, scratchings, deductions — not from a
 * scoreboard.
 */
export const SETTLED_FROM_RESULTS = 'Settled from results';

/**
 * The three racing codes, in the order they are offered: the key the
 * preference is remembered under, the code a row actually carries, and the word
 * a chip prints.
 *
 * ONE LIST, THREE VOCABULARIES, because the app already speaks all three and
 * this is the only place they meet. `key` is PuntersEdge's category, which is
 * what `pref.racingCodes` has been keyed on since the Racing tab shipped;
 * `code` is what `sportCode` squashes a leg's sport down to, which is what a
 * row prints and what the filter compares; `label` is the sport's own name.
 *
 * The labels are typed here rather than imported from `racing/codes.js` for the
 * reason the whole file is a leaf: that module reaches through the racing board
 * to the bookmaker table, and this one has to stay readable in a node test with
 * nothing around it. `history-view.test.js` pins all three against `codeLabel`,
 * so a rename there cannot leave a second spelling behind here.
 */
export const RACING_KINDS = Object.freeze([
  Object.freeze({ key: 'horse', code: 'HR', label: 'Horses' }),
  Object.freeze({ key: 'greyhound', code: 'GR', label: 'Greyhounds' }),
  Object.freeze({ key: 'harness', code: 'HA', label: 'Harness' }),
]);

/** The three racing codes, as `sportCode` prints them. */
const RACING_CODES = new Set(RACING_KINDS.map((k) => k.code));

/**
 * Is every leg on this row a race? All of them, not any: a ticket with an AFL
 * leg and a Randwick leg was settled from a scoreboard AND a result, and the one
 * of the two sentences that is wholly true of it is the one it already had.
 */
const allRacing = (row) => {
  const codes = row?.sports ?? [];
  return codes.length > 0 && codes.every((c) => RACING_CODES.has(c));
};

/**
 * The quiet line under a copy's figures, or null for the rows that have nothing
 * honest to say.
 *
 * 'unresulted' gets none: the panel already says no score came back and offers
 * the legs to mark, and "waiting for scores" under it would be the same row
 * claiming two different things. A settled row with no source is a record from
 * before the app kept one — it is not evidence of either answer, so it makes no
 * claim rather than guessing.
 */
export function settledLine(row) {
  if (row?.result === 'pending') return allRacing(row) ? 'Waiting for the result' : 'Waiting for scores';
  if (row?.result === 'unresulted') return null;
  const by = SETTLED_BY[row?.resultSource] ?? null;
  return by === SETTLED_BY.auto && allRacing(row) ? SETTLED_FROM_RESULTS : by;
}

/**
 * Why a row is stamped "No result", and what to do about it.
 *
 * TWO DIFFERENT SILENCES. A football score that never came back is a gap nobody
 * is going to fill, and the way out is the punter marking the legs themselves. A
 * race that has run and been published as `interim` is not a gap at all — the
 * result is in and the app is refusing to settle off it until the placings have
 * held still, because a protest or a photo moves them. Telling a punter to mark
 * that one by hand would be asking them to overrule a result that is on its way.
 */
export function unresultedNote(legs) {
  if ((legs ?? []).some((l) => l?.result == null && l?.awaitingFinal)) return INTERIM_NOTE;
  return 'No score came back for this bet. Tap a leg to mark it yourself.';
}

// --- what a racing leg was actually paid at ------------------------------------
//
// Three things can happen to a racing bet that cannot happen to a football one,
// and all three change what came back without changing what the stamp says. A
// ticket that reads "Won $18.50" when the ticket beside it at the same odds paid
// $37 is a ticket the punter will think the app got wrong, so the reason is
// printed under it in the app's own quiet voice.
//
// Read off the SETTLED LEG, never recomputed: `stores/settlement.js` writes the
// factor and its reasons onto the leg once, at settle, because a deduction is
// the bookmaker's own figure on the day and next week's feed has forgotten it.

/** "half" / "a third" — what one runner of a dead heat was paid at. */
const heatShare = (n) => ({ 2: 'half', 3: 'a third', 4: 'a quarter' }[n] ?? `1/${n}`);

/** "runner 7" / "runners 7 and 9" — the scratchings a deduction came from. */
function runnersPhrase(selections) {
  const numbers = (selections ?? []).map((s) => String(s).replace(/^r/, '')).filter(Boolean);
  if (!numbers.length) return null;
  if (numbers.length === 1) return `runner ${numbers[0]}`;
  return `runners ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
}

/**
 * The quiet notes a settled racing ticket owes its reader, in the order they
 * happened to the money. Deduplicated, because a four-leg all-up that dead-heated
 * twice has one thing to say, not two.
 *
 * Empty for every ticket that has nothing to explain — which is every football
 * ticket ever settled, and most racing ones.
 */
export function settleNotes(legs) {
  const out = [];
  const add = (note) => { if (note && !out.includes(note)) out.push(note); };

  for (const leg of legs ?? []) {
    if (leg?.voidReason === 'abandoned') add('Race abandoned · stake returned');
    if (leg?.deadHeat > 1) add(`Dead heat · paid at ${heatShare(leg.deadHeat)}`);
    if (Number.isFinite(leg?.deduction) && leg.deduction > 0) {
      const who = runnersPhrase(leg.deductionRunners);
      const pct = Math.round(leg.deduction * 100);
      add(who ? `Deduction ${pct}% · ${who} scratched` : `Deduction ${pct}% · a runner was scratched`);
    }
  }
  return out;
}

/**
 * The risk in ONE word, for a row that has to spell it out beside everything
 * else it is spelling out.
 *
 * `TIER_LABEL` is the long form — "Low risk" — because a ticket has room to say
 * it and a ticket is where somebody decides. In a table the word "risk" is
 * repeated down the whole column and carries nothing; the tier name alone still
 * says it in words, which is what the colour is forbidden from doing on its
 * own. Derived from `TIER_LABEL` rather than written out again, so a tier
 * renamed there cannot leave a second spelling behind here.
 */
export const riskShort = (tier) => (TIER_LABEL[tier] ?? 'Ticket').replace(/\s+risk$/i, '');

/** The long form, for the row's aria-label and for search. */
export const riskLabel = (tier) => TIER_LABEL[tier] ?? 'Ticket';

// --- what sport a leg is ------------------------------------------------------
//
// A leg says which sport it is in one of two fields and neither is a code. The
// engine writes `sportTitle` from the competition it came from ("AFL (demo)")
// and `sport` as its own id ('afl'); a Classic leg carries The Odds API's
// `sport_title`, which is sometimes already a code ("NRL", "MLB") and sometimes
// a sentence ("Big Bash League", "La Liga - Spain"). A column three characters
// wide has to take all of it.

/** "AFL (demo)" → "AFL": a parenthetical is a note about the source, not a name. */
const PARENTHETICAL = /\s*\([^)]*\)\s*$/;

/** "La Liga - Spain" → "La Liga": the provider appends the country with a dash. */
const TRAILING_QUALIFIER = /\s+[-–—]\s+.*$/;

/**
 * Skipped when INITIALS are taken — "National Basketball Association" is NBA —
 * and never removed from the name itself. Take "La" out of "La Liga" and what
 * is left is "Liga", which is not what anybody calls it; the two jobs look like
 * one list and are not.
 */
const JOINING_WORDS = new Set(['of', 'the', 'and', 'a', 'de', 'du', 'la', 'le']);

/**
 * The grade appended to a league's name. The provider tells "A-League Men" from
 * "A-League Women"; this column answers which SPORT a leg is in, and those are
 * the same sport. Dropped from the end only, where a grade is the one thing it
 * can be.
 */
const TRAILING_GRADE = /\s+(men|women)\s*$/i;

/**
 * The leagues worth naming by hand, keyed by their squashed form so 'A-League',
 * 'a league' and 'A-League Men' all land on one entry.
 *
 * Everything else goes through the rules below. This map is for the two cases a
 * rule gets wrong: a name whose initials are not what anybody calls it
 * (Premier League is "EPL", not "PL") and one whose initials collide with how
 * it is already known.
 */
const KNOWN = {
  afl: 'AFL',
  nrl: 'NRL',
  nbl: 'NBL',
  bbl: 'BBL',
  nba: 'NBA',
  nfl: 'NFL',
  mlb: 'MLB',
  nhl: 'NHL',
  epl: 'EPL',
  aleague: 'A-League',
  'a league': 'A-League',
  'premier league': 'EPL',
  'english premier league': 'EPL',
  'big bash': 'BBL',
  'big bash league': 'BBL',
  'major league baseball': 'MLB',
  'national basketball association': 'NBA',
  'champions league': 'UCL',
  'uefa champions league': 'UCL',
  // The three racing codes. "Horses" is six letters, so without an entry the
  // rules below would print the word itself in a column sized for a code.
  horses: 'HR',
  greyhounds: 'GR',
  harness: 'HA',
};

/** Lowercase, alphanumerics joined by single spaces — the shape KNOWN is keyed by. */
const squash = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * A sport's short code: "NRL", "EPL", "A-League".
 *
 * Known names first, then two rules. A single short word is already a code and
 * only needs its case ("nrl" → "NRL"). Anything longer becomes its initials,
 * but ONLY if that leaves at least three letters — "Super Rugby" as "SR" and
 * "La Liga" as "LL" are worse than printing the name, so those keep the name
 * and the column carries a wider cell for them. Null when there is nothing to
 * print, so a leg with no sport on it drops out rather than showing a blank.
 */
export function sportCode(value) {
  const raw = String(value ?? '')
    .replace(PARENTHETICAL, '')
    .replace(TRAILING_QUALIFIER, '')
    .replace(TRAILING_GRADE, '')
    .trim();
  if (!raw) return null;

  const known = KNOWN[squash(raw)];
  if (known) return known;

  const words = raw.split(/\s+/);
  // One word is either already a code ("NRL") or the name itself ("Cricket").
  if (words.length === 1) return words[0].length <= 5 ? words[0].toUpperCase() : words[0];

  const initials = words
    .filter((w) => !JOINING_WORDS.has(w.toLowerCase()))
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  // Two letters is not a code anybody reads — "SR" for Super Rugby, "LL" for La
  // Liga — so the name stands and the cell carries the extra characters.
  return initials.length >= 3 ? initials : raw;
}

/** What one leg calls its sport. The competition beats the engine's own id. */
const legSport = (leg) => leg?.sportTitle ?? leg?.sport ?? null;

/**
 * The distinct sports on a ticket, in the order its legs print them.
 *
 * Distinct is the whole point: a six-leg NRL multi is one sport, and a column
 * that printed "NRL · NRL · NRL" would be six legs pretending to be six
 * competitions.
 */
export function sportCodes(legs) {
  const out = [];
  for (const leg of legs ?? []) {
    const code = sportCode(legSport(leg));
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

/** The sport titles as written, kept off to one side so search can still find them. */
export function sportTitles(legs) {
  const out = [];
  for (const leg of legs ?? []) {
    const title = legSport(leg);
    if (title && !out.includes(title)) out.push(title);
  }
  return out;
}

/**
 * "NRL", "NRL · EPL", "NRL · EPL +2".
 *
 * Two codes is what a row has room for at 320px; past that the count is more
 * honest than a truncated third, because "+2" says there are more where a
 * clipped "MLB…" says the row ran out of paper.
 */
export function sportsLabel(codes, max = 2) {
  const list = codes ?? [];
  if (!list.length) return '';
  if (list.length <= max) return list.join(' · ');
  return `${list.slice(0, max).join(' · ')} +${list.length - max}`;
}

// --- rows ---------------------------------------------------------------------

/** A finite number, or null — so a comparator never has to think about ''. */
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * A moment in milliseconds. A bare 'YYYY-MM-DD' is a LOCAL calendar day: read
 * plain, `new Date` takes it as UTC midnight and dates the row to the day
 * before anywhere west of Greenwich — the same trap `day()` documents.
 */
function whenMs(value) {
  if (!value) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * What a row's result actually READS as, which is not always what is stored.
 *
 * Pending and overdue is 'unresulted' — "No result". Nothing else moves: a bet
 * the punter or the settle has already decided says what it was decided as,
 * and a bet whose matches have not been played yet is honestly Pending.
 *
 * This is the ONLY place the derived state is made, so a bet and a proposal
 * cannot drift apart on what "no score came back" means, and the storage under
 * both is left exactly as it was.
 */
const resultState = (stored, legs, now) =>
  (stored === 'pending' && legsOverdue(legs, now) ? 'unresulted' : stored);

/**
 * One carbon copy as a row.
 *
 * `now` is passed in rather than read: everything else in this file is pure
 * over its arguments, and a clock read inside it would make "what does this row
 * say" a question with a different answer every morning at seven and no way to
 * test either of them.
 *
 * `strategyName` is passed in rather than looked up: resolving a strategy id to
 * a name means reading the saved strategies, which is a store, which is a Vue
 * ref — and this file stays runnable in a node test. The multi's own `label` is
 * the fallback, and it is the right one: a strategy deleted since is still the
 * strategy this ticket was printed by.
 */
export function betRow({ bet, ticket, strategyName, now = Date.now() } = {}) {
  const multi = ticket?.multi ?? null;
  const legs = ticket?.legs ?? [];
  const codes = sportCodes(legs);
  const stake = num(bet?.stake);
  const returned = num(bet?.returned);
  const stored = RESULT_LABEL[bet?.result] ? bet.result : 'pending';
  const result = resultState(stored, legs, now);
  const strategy = strategyName || multi?.label || '';

  return {
    id: bet?.id ?? null,
    kind: 'bet',
    multiId: bet?.multiId ?? null,
    tier: multi?.tier ?? null,
    risk: riskShort(multi?.tier),
    riskLabel: riskLabel(multi?.tier),
    strategy,
    strategyId: multi?.strategyId ?? null,
    // What the filter compares. A ticket printed before strategies existed has
    // no id, so the name stands in — and `filterOptions` builds its values the
    // same way, which is what keeps the two in step. Canonicalised, so one
    // "Market edge" in the filter picks up the rows printed under the three
    // ids it was merged from; `strategyId` above keeps what was stored.
    strategyKey: canonicalStrategyId(multi?.strategyId) ?? strategy ?? '',
    sports: codes,
    sportTitles: sportTitles(legs),
    sportsText: sportsLabel(codes),
    result,
    resultLabel: RESULT_LABEL[result],
    odds: num(multi?.combinedOdds),
    stake,
    returned,
    // Nothing has come back on a pending bet, so its P&L is not zero — it is
    // not known yet, and a zero would sort it in among the breaking-evens. The
    // STORED result is what decides that, not the derived one: a row reading
    // "No result" is a pending bet that has been waiting too long, and it has
    // no more of a P&L than it did yesterday.
    pnl: stored === 'pending' || returned === null || stake === null ? null : returned - stake,
    at: whenMs(bet?.placedAt) ?? whenMs(multi?.forDate),
    forDate: multi?.forDate ?? null,
    teams: legs.map((l) => l?.selection).filter(Boolean),
    legCount: legs.length,
    resultSource: bet?.resultSource ?? null,
  };
}

/**
 * One proposal as a row — the same shape, so one table renders both piles.
 *
 * A proposal was never placed, so stake, returns and P&L are null rather than
 * zero: the pad printed it, nobody backed it, and a column of $0.00 down the
 * Proposed tab would read as a season of losing bets.
 */
export function proposalRow(proposal, { now = Date.now() } = {}) {
  const multi = proposal?.multi ?? null;
  const legs = proposal?.legs ?? [];
  const codes = sportCodes(legs);
  const stored = RESULT_LABEL[proposal?.outcome] ? proposal.outcome : 'pending';
  const result = resultState(stored, legs, now);
  const strategy = proposal?.strategyName || multi?.label || '';

  return {
    id: proposal?.id ?? null,
    kind: 'proposal',
    multiId: multi?.id ?? null,
    tier: multi?.tier ?? null,
    risk: riskShort(multi?.tier),
    riskLabel: riskLabel(multi?.tier),
    strategy,
    strategyId: proposal?.strategyId ?? multi?.strategyId ?? null,
    strategyKey: canonicalStrategyId(proposal?.strategyId ?? multi?.strategyId) ?? strategy ?? '',
    sports: codes,
    sportTitles: sportTitles(legs),
    sportsText: sportsLabel(codes),
    result,
    resultLabel: RESULT_LABEL[result],
    odds: num(multi?.combinedOdds),
    stake: null,
    returned: null,
    pnl: null,
    clv: num(proposal?.clv),
    at: whenMs(proposal?.date),
    forDate: proposal?.date ?? null,
    teams: legs.map((l) => l?.selection).filter(Boolean),
    legCount: legs.length,
    source: proposal?.source ?? null,
    rank: proposal?.rank ?? null,
    setIndex: Number.isFinite(proposal?.setIndex) ? proposal.setIndex : null,
    superseded: proposal?.superseded === true,
  };
}

// --- search -------------------------------------------------------------------

/**
 * Everything about a row that a person might type. Built per call rather than
 * stored on the row: it is only ever needed while somebody is searching, and a
 * stored copy is a second thing to keep in step with the row it describes.
 */
const haystack = (row) => squash([
  row?.teams?.join(' '),
  row?.strategy,
  row?.sportTitles?.join(' '),
  row?.sports?.join(' '),
  row?.riskLabel,
  row?.resultLabel,
].filter(Boolean).join(' '));

/**
 * Rows matching a typed query — team or selection names, the strategy, the
 * sports, the risk, the result. Case-insensitive and trimmed, and punctuation
 * on either side is flattened to a space, so "a-league" finds "A-League" and
 * "st kilda" finds "St. Kilda".
 *
 * An empty query is not a filter: it returns everything, rather than nothing.
 */
export function searchBets(rows, query) {
  const list = rows ?? [];
  const q = squash(query);
  if (!q) return [...list];
  return list.filter((row) => haystack(row).includes(q));
}

// --- filter -------------------------------------------------------------------

/**
 * Rows kept by the filters above the list. An absent, empty or 'all' value is
 * not a filter — there is one way to mean "everything" and all three spell it.
 *
 * Sport matches against the CODES, which is what the row prints and what the
 * options offer. A multi spanning NRL and MLB is kept by either.
 *
 * Result matches the DERIVED state, so "No result" is a thing you can ask for
 * — which is the point of naming it at all: a punter who wants the bets the
 * server never answered on wants exactly that list, and on the Pending pile
 * they are otherwise mixed in among the ones still being played.
 */
export function filterBets(rows, { strategy, sport, result } = {}) {
  const wantStrategy = strategy && strategy !== 'all' ? strategy : null;
  const wantSport = sport && sport !== 'all' ? sport : null;
  const wantResult = result && result !== 'all' ? result : null;
  return (rows ?? []).filter((row) => {
    if (wantStrategy && row?.strategyKey !== wantStrategy) return false;
    if (wantSport && !(row?.sports ?? []).includes(wantSport)) return false;
    if (wantResult && row?.result !== wantResult) return false;
    return true;
  });
}

/**
 * What the two filters can offer, built from the rows actually in the pile.
 *
 * Never the whole catalogue of strategies or the twelve sports: a filter that
 * offers a value matching nothing is a dead end somebody has to back out of,
 * and on the Resulted pile most of them would be.
 */
export function filterOptions(rows) {
  const strategies = new Map();
  const sports = new Set();
  const results = new Set();
  for (const row of rows ?? []) {
    // The FIRST name a key is seen under, not the last. Rows arrive newest
    // first and a key can now collect rows printed under a retired id, whose
    // frozen label says what the strategy used to be called; letting an older
    // row overwrite would name the filter after the name that is gone.
    if (row?.strategyKey && row.strategy && !strategies.has(row.strategyKey)) {
      strategies.set(row.strategyKey, row.strategy);
    }
    for (const code of row?.sports ?? []) sports.add(code);
    if (RESULT_LABEL[row?.result]) results.add(row.result);
  }
  return {
    strategies: [...strategies.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    sports: [...sports].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value })),
    // In the sort's own order rather than alphabetically: Won, Void, Lost, No
    // result, Pending is how the column already reads, and a list of results
    // ordered W, L, N, P, V would be a second opinion about the same five words.
    results: [...results]
      .sort((a, b) => (RESULT_ORDER[a] ?? 9) - (RESULT_ORDER[b] ?? 9))
      .map((value) => ({ value, label: RESULT_LABEL[value] })),
  };
}

// --- two kinds of paper, and three codes of one of them -----------------------
//
// The same cut Today's feed takes (`lib/feed.js`), one step finer. Today asks
// "is this the football or the horses"; History is where somebody reads a
// season back, and the question there is often "how have the greyhounds gone" —
// so the racing half divides again into the three codes.
//
// IT WORKS OFF THE ROW, not off the legs. `row.sports` is already the distinct
// codes of every leg, derived once when the row was built; reaching back
// through the record into the frozen multi for every row on every tap is the
// three lookups per row this whole file exists to avoid.

/** Every code on, as an object. The shape a stored preference is read into. */
export const ALL_RACING_CODES = Object.freeze(
  Object.fromEntries(RACING_KINDS.map((k) => [k.key, true])),
);

/** Everything: the filter `filterHistory` is a no-op for. */
export const HISTORY_FILTER_ALL = Object.freeze({
  sports: true,
  racing: true,
  codes: ALL_RACING_CODES,
});

/**
 * The codes a filter is asking for, as the codes a row carries.
 *
 * An absent or half-written `codes` reads as all three: the failure of a filter
 * has to be "it forgot", never "it hid something".
 */
const wantedCodes = (codes) =>
  new Set(RACING_KINDS.filter((k) => codes?.[k.key] !== false).map((k) => k.code));

/**
 * Which kinds of leg a row carries: the racing codes on it, and whether any of
 * it is sport.
 *
 * A ROW WITH NO SPORT ON IT AT ALL IS SPORT, the same rule `kindsOf` keeps on
 * the feed and for the same reason: every leg the engine prints carries one,
 * and the only rows that can arrive without are old records rebuilt from a
 * frozen copy, which are sports-board tickets to the last one. Counting them as
 * nothing would make a legacy bet vanish the moment either toggle moved.
 */
export function kindsOfRow(row) {
  const codes = row?.sports ?? [];
  return {
    racing: codes.filter((c) => RACING_CODES.has(c)),
    sports: codes.length === 0 || codes.some((c) => !RACING_CODES.has(c)),
  };
}

/**
 * The rows a kind filter leaves, in the order they came.
 *
 * A MIXED TICKET IS BOTH. A multi with a race and a football match on it shows
 * whenever either switch is on — it is half the reader's, and hiding it would
 * be the filter throwing away a ticket neither switch was asked about. The same
 * holds one level down: a two-race all-up over a horse and a greyhound shows
 * while either chip is on.
 *
 * Separate from `filterBets` rather than folded into it: that one is the three
 * selects in the sift block, which are a single-value each and clear
 * themselves; this is a remembered reading preference that outlives the pile it
 * was set on, and the screen has to be able to say which of the two emptied it.
 */
export function filterHistory(rows, { sports = true, racing = true, codes = null } = {}) {
  const want = wantedCodes(codes);
  if (sports && racing && want.size === RACING_KINDS.length) return [...(rows ?? [])];
  return (rows ?? []).filter((row) => {
    const kinds = kindsOfRow(row);
    if (kinds.sports && sports) return true;
    return racing && kinds.racing.some((c) => want.has(c));
  });
}

/**
 * How many rows carry each code, keyed the way the preference is: what the
 * chips print beside their names.
 *
 * A mixed ticket is counted under every code it holds, which is the same answer
 * the filter gives — a chip that says 12 and shows 14 rows would be the count
 * and the cut disagreeing about one ticket.
 */
export function racingCounts(rows) {
  const out = Object.fromEntries(RACING_KINDS.map((k) => [k.key, 0]));
  for (const row of rows ?? []) {
    const codes = row?.sports ?? [];
    for (const k of RACING_KINDS) if (codes.includes(k.code)) out[k.key] += 1;
  }
  return out;
}

/** Is this filter hiding nothing at all? */
export const showsEverything = ({ sports = true, racing = true, codes = null } = {}) =>
  sports && racing && wantedCodes(codes).size === RACING_KINDS.length;

/** "sports", "sports and racing", "sports, horses and harness". */
const andList = (words) => (words.length <= 1
  ? words[0] ?? ''
  : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

/**
 * The three codes as a chooser: what each box says, what it counts and whether
 * it is ticked. "Horses 12".
 *
 * THE COUNT IS THE POINT. Three names on their own are three guesses about
 * which one is worth looking at; the counts are what tell somebody the season
 * they are reading is four fifths horses, before they tick anything.
 */
export function codeChoices(codes, counts) {
  return RACING_KINDS.map((k) => ({
    key: k.key,
    label: `${k.label} ${counts?.[k.key] ?? 0}`,
    name: k.label,
    count: counts?.[k.key] ?? 0,
    on: codes?.[k.key] !== false,
  }));
}

/**
 * What the chip that opens them says: the bare word while it is holding nothing
 * back, and a count of three once it is.
 *
 * A COUNT RATHER THAN THE NAMES, and that is a width decision with a measurement
 * behind it. The chip shares its row with the three piles and the two kind
 * toggles; at 375 the segment needs 159px of the 229 the two of them have
 * between them, which leaves the chip 70. "Greyhounds" is 76 and printing it
 * took two of the three pile names down to "Result…". The names are on the
 * `aria-label` and in the sheet the chip opens, where there is room for them.
 */
export function codesChipLabel(codes) {
  const on = RACING_KINDS.filter((k) => codes?.[k.key] !== false);
  if (on.length === RACING_KINDS.length) return 'Codes';
  return `${on.length} of ${RACING_KINDS.length}`;
}

/**
 * The same, spelled out — a chip reading "2 codes" tells a screen reader
 * nothing at all about which two.
 */
export function codesChipAria(codes) {
  const on = RACING_KINDS.filter((k) => codes?.[k.key] !== false);
  if (on.length === RACING_KINDS.length) return 'Which racing codes: all three showing';
  if (!on.length) return 'Which racing codes: none showing';
  return `Which racing codes: ${andList(on.map((k) => k.label))} showing`;
}

/**
 * What the filter is showing, in words, for the picture and its caption.
 *
 * EMPTY WHEN NOTHING IS HIDDEN, so the line only ever prints where it is
 * telling the reader something they cannot see for themselves. A shared card is
 * read by somebody who was not holding the phone: without this they would take
 * a racing-only pile for the whole season.
 */
export function filterLine({ sports = true, racing = true, codes = null } = {}) {
  const kept = RACING_KINDS.filter((k) => codes?.[k.key] !== false);
  const whole = kept.length === RACING_KINDS.length;
  if (sports && racing && whole) return '';
  const parts = [];
  if (sports) parts.push('sports');
  if (racing && kept.length) parts.push(...(whole ? ['racing'] : kept.map((k) => k.label.toLowerCase())));
  return parts.length ? `Showing ${andList(parts)}` : 'Nothing shown';
}

// --- sort ---------------------------------------------------------------------

/**
 * Decided before undecided, and the decided ones in the order they hurt.
 *
 * 'unresulted' sits between the decided ones and 'pending', which is where it
 * belongs in both readings of the column. It is nearer the settled ones,
 * because a bet nobody is going to score again is finished with the market
 * whatever the stamp says; and it is still ahead of Pending, because Pending
 * is the only state that will resolve itself.
 *
 * The same instinct as `sortBets`'s "what is missing sinks", one step along:
 * that rule is about a row with no VALUE for the field being sorted on, and
 * both of these have a value. Where they have none — P&L, which neither of them
 * has — they sink together, which is correct and needs no extra rule.
 */
const RESULT_ORDER = { won: 0, void: 1, lost: 2, unresulted: 3, pending: 4 };

const byNumber = (a, b) => a - b;
const byText = (a, b) => String(a).localeCompare(String(b));

/**
 * The sorts the table offers, and what each direction is CALLED.
 *
 * The direction is named rather than arrowed because "newest first" and
 * "longest first" are what somebody is actually choosing between — an arrow
 * pointing down next to the word "Odds" leaves them to work out which end of
 * the odds it means.
 */
export const SORTS = [
  { key: 'date', label: 'Date', desc: 'Newest first', asc: 'Oldest first', value: (r) => r.at, compare: byNumber },
  { key: 'odds', label: 'Odds', desc: 'Longest first', asc: 'Shortest first', value: (r) => r.odds, compare: byNumber },
  { key: 'stake', label: 'Stake', desc: 'Biggest first', asc: 'Smallest first', value: (r) => r.stake, compare: byNumber },
  { key: 'pnl', label: 'P&L', desc: 'Best first', asc: 'Worst first', value: (r) => r.pnl, compare: byNumber },
  { key: 'result', label: 'Result', desc: 'Losses first', asc: 'Wins first', value: (r) => RESULT_ORDER[r.result] ?? 9, compare: byNumber },
  { key: 'strategy', label: 'Strategy', desc: 'Z to A', asc: 'A to Z', value: (r) => r.strategy || null, compare: byText },
];

const SORT_BY_KEY = new Map(SORTS.map((s) => [s.key, s]));

/** The sort a key names, falling back to the default rather than to nothing. */
export const sortSpec = (key) => SORT_BY_KEY.get(key) ?? SORT_BY_KEY.get('date');

/** What the direction toggle should say for the sort now in force. */
export const directionLabel = (key, dir) => (dir === 'asc' ? sortSpec(key).asc : sortSpec(key).desc);

const missing = (v) => v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

/**
 * Rows in an order, newest first by default. Never sorts in place — the caller
 * is holding a computed list, and a sort that mutated it would reorder the pile
 * underneath whatever else is reading it.
 *
 * WHAT IS MISSING SINKS, IN BOTH DIRECTIONS. A pending bet has no P&L and a
 * void ticket has no odds; reversing the sort must not float the blanks to the
 * top, because "worst first" asking for the rows with no answer yet is not what
 * anybody means by worst. Ties keep the order they arrived in — the piles come
 * out of the store newest first, so equal odds stay newest first.
 */
export function sortBets(rows, key = 'date', dir = 'desc') {
  const spec = sortSpec(key);
  const sign = dir === 'asc' ? 1 : -1;
  return [...(rows ?? [])].sort((a, b) => {
    const av = spec.value(a);
    const bv = spec.value(b);
    const an = missing(av);
    const bn = missing(bv);
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    return spec.compare(av, bv) * sign;
  });
}

/**
 * The whole pipeline, in the one order that is correct: search and filter first,
 * sort what is left.
 *
 * Sorting before filtering would sort rows that are about to be thrown away —
 * on the Resulted pile that is the whole season every keystroke.
 */
export function viewRows(rows, { query = '', strategy = null, sport = null, result = null, sort = 'date', dir = 'desc' } = {}) {
  return sortBets(filterBets(searchBets(rows, query), { strategy, sport, result }), sort, dir);
}
